import { useMemo, useState } from 'react';
import { HelpLink } from '../help/HelpLink';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  categoriesText,
  asksStandpoint,
  DAMAGE_STATUSES,
  de,
  findNearby,
  hasAtLeastRole,
  isTurned,
  NEARBY_VISIBLE_STATUSES,
  participationAllows,
  statusLabel,
  viewLabel,
  type AircraftView,
  type DamageStatus,
  type NormalizedPoint,
  type Standpoint,
} from '@fsv/core';
import { Chip, Segmented } from '../components/Controls';
import { CreateDamageSheet } from '../components/DamageSheets';
import { NoticePanel } from '../components/NoticePanel';
import { DamageRow } from '../components/DamageRow';
import { DrawingBoard } from '../components/DrawingBoard';
import { Icon } from '../components/Icon';
import { Sheet } from '../components/Sheet';
import { StandpointPrompt } from '../components/StandpointPrompt';
import { StatusBadge, StatusGlyph } from '../components/Status';
import { useToast } from '../components/Toast';
import { useAircraftList, useDamage, useDamages, useSettings, useTenant, useTenantPath, useDamageRef } from '../data/hooks';
import type { Damage } from '@fsv/data';
import { useDrawing } from '../drawings/useDrawing';
import { busiestView, countsByView, DEFAULT_PLAN_FILTER, PLAN_VIEWS, tx } from '@fsv/data';
import { formatDate } from '@fsv/data';
import { DESKTOP, useMediaQuery } from '../lib/useMediaQuery';
import { ErrorState, Loading } from './states';
import './PlanScreen.css';

const VIEWS: readonly AircraftView[] = PLAN_VIEWS;

function isView(v: string | null): v is AircraftView {
  return v !== null && (VIEWS as readonly string[]).includes(v);
}

function PinSheet({ damage, onClose }: { damage: Damage; onClose: () => void }) {
  const ref = useDamageRef();
  const detail = useDamage(damage.id);
  const images = detail.data?.images ?? [];
  return (
    <Sheet
      title={damage.title}
      kicker={
        <span className="row">
          <span className="mono pin-sheet__num">{ref(damage.number)}</span>
          <StatusBadge status={damage.status} />
        </span>
      }
      onClose={onClose}
      footer={
        <Link className="btn btn--primary" to={`/schaden/${damage.id}`}>
          {tx.plan.toDetail}
          <Icon name="forward" />
        </Link>
      }
    >
      <p className="muted">
        {categoriesText(damage.categories)} · {viewLabel[damage.view]} · {damage.reportedBy.name}, {formatDate(damage.reportedAt)}
      </p>
      {images.length > 0 && (
        <ul className="pin-sheet__photos">
          {images.slice(0, 4).map((img) => (
            <li key={img.id}>
              <img src={img.thumbUrl} alt="" />
            </li>
          ))}
        </ul>
      )}
      {damage.description && <p className="pin-sheet__desc">{damage.description}</p>}
      {damage.reviewNote && (
        <p className="notice">
          <Icon name="message" />
          {damage.reviewNote}
        </p>
      )}
    </Sheet>
  );
}

function ClusterSheet({ damages, onPick, onClose }: { damages: Damage[]; onPick: (d: Damage) => void; onClose: () => void }) {
  return (
    <Sheet title={tx.plan.cluster(damages.length)} onClose={onClose}>
      <div className="stack">
        {damages.map((d) => (
          <DamageRow key={d.id} damage={d} showAircraft={false} onSelect={() => onPick(d)} />
        ))}
      </div>
    </Sheet>
  );
}

function Legend() {
  return (
    <details className="legend">
      <summary>
        <Icon name="list" size={18} />
        {tx.plan.legend}
      </summary>
      <ul className="legend__list">
        {DAMAGE_STATUSES.filter((s) => s !== 'verworfen').map((s) => (
          <li key={s} className={`st--${s}`}>
            <StatusGlyph status={s} size={16} />
            {statusLabel[s]}
          </li>
        ))}
        <li>
          <Icon name="mirror" size={16} />
          {tx.plan.mirrored}: {viewLabel.unten}, {viewLabel.rechts}
        </li>
      </ul>
    </details>
  );
}

export function PlanScreen({ mode = 'plan' }: { mode?: 'plan' | 'list' }) {
  const damageRef = useDamageRef();
  const { reg = '' } = useParams();
  const registration = reg.toUpperCase();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const desktop = useMediaQuery(DESKTOP);
  const role = useTenant().role;
  const to = useTenantPath();
  const isEditor = hasAtLeastRole(role, 'editor');

  const viewParam = params.get('view');
  const selectedId = params.get('pin');

  const aircraftList = useAircraftList();
  const damages = useDamages(registration);
  const [filter, setFilter] = useState<DamageStatus[]>([...DEFAULT_PLAN_FILTER]);
  const counts = useMemo(() => countsByView(damages.data ?? [], filter), [damages.data, filter]);
  // Without ?view= the plan opens on the busiest view — decided once, when the damages first arrive, so it never jumps.
  const [opening, setOpening] = useState<AircraftView | null>(null);
  if (opening === null && damages.data) setOpening(busiestView(counts));
  const view: AircraftView = isView(viewParam) ? viewParam : (opening ?? 'oben');
  const settings = useSettings();
  const aircraft = aircraftList.data?.find((a) => a.registration === registration);
  const resolved = useDrawing(aircraft?.typeCode, view);

  const [scope, setScope] = useState<'view' | 'all'>('view');
  // The crosshair stays on the drawing until "Hier melden"; until then it can be moved or set anew.
  const [point, setPoint] = useState<NormalizedPoint | null>(null);
  const [reporting, setReporting] = useState(false);
  const closeReport = () => {
    setReporting(false);
    setPoint(null);
    // Sent or cancelled, the next report asks „Wo stehst du?“ again.
    setStand(null);
  };
  const [summary, setSummary] = useState<Damage | null>(null);
  const [cluster, setCluster] = useState<Damage[] | null>(null);

  const all = useMemo(() => damages.data ?? [], [damages.data]);
  const inView = useMemo(() => all.filter((d) => d.view === view), [all, view]);
  const pins = useMemo(() => inView.filter((d) => d.status !== 'verworfen' && filter.includes(d.status)), [inView, filter]);
  const listed = useMemo(
    () => (scope === 'view' ? inView : all).filter((d) => filter.includes(d.status) || (filter.includes('verworfen') && d.status === 'verworfen')),
    [scope, inView, all, filter],
  );
  const countIn = (s: DamageStatus) => inView.filter((d) => d.status === s).length;

  const setView = (v: AircraftView, pin?: string) => {
    const next = new URLSearchParams(params);
    next.set('view', v);
    if (pin) next.set('pin', pin);
    else next.delete('pin');
    setParams(next, { replace: true });
  };

  // Until the settings are there, the default (report): the API refuses what the club switched off.
  const canReport = participationAllows(role, settings.data?.memberParticipation, 'report');

  // „Wo stehst du?“ on the top and bottom views, asked again for every report
  // (owner, 17.10.2026): one answer counts for both views of this aircraft —
  // you stand in the same place — until the report is sent.
  const [stand, setStand] = useState<{ registration: string; at: Standpoint } | null>(null);
  const standpoint = stand?.registration === registration ? stand.at : null;
  const asksHere = canReport && asksStandpoint(view);
  const turned = isTurned(view, standpoint);

  const onTapEmpty = (point: NormalizedPoint) => {
    if (!canReport) {
      toast.show(de.participation.reportOff, { tone: 'error' });
      return;
    }
    // Always taken: a tap dropped while the settings load looks like a dead board. "Hier melden" waits for them instead.
    setPoint(point);
  };
  const nearby = useMemo(() => {
    if (!point || !resolved || !settings.data) return [];
    const candidates = inView.filter((d) => NEARBY_VISIBLE_STATUSES.includes(d.status));
    return findNearby(point, candidates, settings.data.duplicateRadius, resolved.aspect).map((h) => h.item);
  }, [point, resolved, settings.data, inView]);

  if (aircraftList.isPending || damages.isPending) return <Loading />;
  if (aircraftList.isError) return <ErrorState error={aircraftList.error} />;
  if (!aircraft || damages.isError) {
    return (
      <div className="page">
        <ErrorState error={damages.error ?? new Error(tx.plan.notFound)} />
        <Link to={to()} className="btn">
          <Icon name="back" />
          {tx.fleet.title}
        </Link>
      </div>
    );
  }

  const viewOptions = VIEWS.map((v) => {
    const mirrored = resolved?.type ? v in resolved.type.mirrorRules : v === 'unten' || v === 'rechts';
    return {
      value: v,
      ariaLabel: [viewLabel[v], mirrored ? `(${tx.plan.mirrored})` : '', tx.plan.viewCount(counts[v].count)].filter(Boolean).join(' '),
      className: [mirrored ? 'view-seg--mirrored' : '', counts[v].count === 0 ? 'view-seg--empty' : ''].filter(Boolean).join(' ') || undefined,
      label: (
        <>
          {viewLabel[v]}
          {mirrored && <Icon name="mirror" size={12} />}
          {counts[v].count > 0 && (
            <span className={`view-count view-count--${counts[v].top ?? 'gemeldet'}`} data-testid={`view-count-${v}`}>
              {counts[v].count}
            </span>
          )}
        </>
      ),
    };
  });

  const showBoard = desktop || mode === 'plan';
  const showList = desktop || mode === 'list';
  const listHref = to(`/flugzeug/${registration}/${mode === 'plan' ? 'liste' : 'plan'}?${params.toString()}`);

  return (
    <div className="plan">
      <div className="plan__bar">
        <Link to={to()} className="btn btn--ghost btn--icon" aria-label={tx.fleet.title}>
          <Icon name="back" />
        </Link>
        <div className="plan__id">
          <h1 className="plan__reg mono">{aircraft.registration}</h1>
          <span className="label">{aircraft.typeName}</span>
        </div>
        <span className="spacer" />
        <HelpLink screen="plan" />
        {!desktop && (
          <Link to={listHref} className="btn" replace>
            <Icon name={mode === 'plan' ? 'list' : 'map'} />
            {mode === 'plan' ? tx.plan.listToggle : tx.plan.planToggle}
          </Link>
        )}
      </div>

      <div className="plan__notices">
        <NoticePanel aircraftId={aircraft.id} registration={aircraft.registration} role={role} />
      </div>

      <div className="plan__grid">
        {showBoard && (
          <section className="plan__main" aria-label={tx.plan.board(aircraft.registration, viewLabel[view])}>
            <div className="plan__views" data-tour="plan-views">
              <Segmented label={tx.plan.viewSwitcher} value={view} options={viewOptions} onChange={(v) => setView(v)} scroll />
            </div>
            {asksHere && standpoint && (
              <p className="plan__standpoint" data-testid="standpoint-shown">
                <Icon name="pin" size={16} />
                <span>{standpoint === 'nose' ? tx.plan.standpoint.noseShown : tx.plan.standpoint.tailShown}</span>
                <button type="button" className="btn btn--ghost" onClick={() => setStand(null)}>
                  {tx.plan.standpoint.change}
                </button>
              </p>
            )}
            <div className="plan__boardarea">
              {resolved ? (
                <DrawingBoard
                  key={`${registration}-${view}-${turned ? 'nose' : 'tail'}`}
                  resolved={resolved}
                  pins={pins}
                  label={tx.plan.board(aircraft.registration, viewLabel[view])}
                  selectedId={selectedId}
                  provisional={point}
                  onTapEmpty={onTapEmpty}
                  onPinTap={setSummary}
                  onClusterTap={setCluster}
                  watermark={[
                    viewLabel[view],
                    resolved.mirrored ? tx.plan.mirroredOf(viewLabel[resolved.base]) : null,
                    turned ? tx.plan.standpoint.watermark : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  credit={resolved.type.credit}
                  damageRef={damageRef}
                  turned={turned}
                />
              ) : (
                <div className="board board--loading" />
              )}
              {asksHere && !standpoint && (
                <StandpointPrompt onPick={(at) => setStand({ registration, at })} />
              )}
            </div>
            <div className="plan__tools">
              {point && !reporting && (
                <div className="plan__place">
                  <button type="button" className="btn" onClick={() => setPoint(null)}>
                    {tx.plan.placeDiscard}
                  </button>
                  <button type="button" className="btn btn--primary" onClick={() => setReporting(true)} disabled={!settings.data || !resolved}>
                    <Icon name="pin" />
                    {isEditor ? tx.plan.placeConfirmEditor : tx.plan.placeConfirm}
                  </button>
                </div>
              )}
              <p className="plan__hint">
                <Icon name="pin" size={18} />
                {canReport ? (point ? tx.plan.placeHintSet : isEditor ? tx.plan.tapHintEditor : tx.plan.tapHint) : de.participation.reportOff}
              </p>
              <div className="plan__filters" role="group" aria-label={tx.plan.filter}>
                {DAMAGE_STATUSES.map((s) => (
                  <Chip key={s} className={`st--${s}`} pressed={filter.includes(s)} onToggle={() => setFilter((f) => (f.includes(s) ? f.filter((x) => x !== s) : [...f, s]))}>
                    <StatusGlyph status={s} size={12} />
                    {statusLabel[s]}
                    <span className="chip__count">{countIn(s)}</span>
                  </Chip>
                ))}
              </div>
              <Legend />
            </div>
          </section>
        )}

        {showList && (
          <aside className="plan__side" aria-label={tx.plan.listTitle(listed.length)}>
            <div className="plan__side-head">
              <h2>{tx.plan.listTitle(listed.length)}</h2>
              <Segmented
                label={tx.plan.listToggle}
                value={scope}
                onChange={setScope}
                options={[
                  { value: 'view', label: tx.plan.thisView },
                  { value: 'all', label: tx.plan.allViews },
                ]}
              />
            </div>
            {listed.length === 0 ? (
              <p className="empty">{tx.plan.listEmpty}</p>
            ) : (
              <div className="stack plan__list">
                {listed.map((d) =>
                  desktop ? (
                    <DamageRow
                      key={d.id}
                      damage={d}
                      showAircraft={false}
                      selected={d.id === selectedId}
                      onSelect={() => {
                        setView(d.view, d.id);
                        setSummary(d);
                      }}
                    />
                  ) : (
                    <DamageRow key={d.id} damage={d} showAircraft={false} />
                  ),
                )}
              </div>
            )}
          </aside>
        )}
      </div>

      {point && reporting && (
        <CreateDamageSheet
          aircraft={aircraft}
          view={view}
          point={point}
          nearby={nearby}
          role={role}
          onClose={closeReport}
          onCreated={(d) => {
            closeReport();
            setView(view, d.id);
          }}
        />
      )}
      {summary && <PinSheet damage={summary} onClose={() => setSummary(null)} />}
      {cluster && (
        <ClusterSheet
          damages={cluster}
          onClose={() => setCluster(null)}
          onPick={(d) => {
            setCluster(null);
            if (desktop) setSummary(d);
            else navigate(to(`/schaden/${d.id}`));
          }}
        />
      )}
    </div>
  );
}
